import { type EditorView } from "@codemirror/view";
import { useCallback, useRef } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { fireUpdateCodeEvent } from "../../lib/editor/events";
import { AiChatLevelAtom, InlineEditTargetAtom } from "../../state/aiChatAtoms";
import { CodeMirrorCodeState } from "../../state/codeAtom";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import InlineCodeMirror from "./InlineCodeMirror";
import {
	InlineCodeMirrorWrapper,
	InlineEditorActions,
	InlineEditorButton,
	InlineEditorContainer,
	InlineEditorContext,
} from "./styled/ChatStyled";

function InlineElementEditor() {
	const [target, setTarget] = useRecoilState(InlineEditTargetAtom);
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const fullCode = useRecoilValue(CodeMirrorCodeState(stackCfKey));
	const setLevel = useSetRecoilState(AiChatLevelAtom);
	const editorViewRef = useRef<EditorView | null>(null);

	// Snapshot the full code when a new edit target arrives so Cancel can restore it
	const originalCodeRef = useRef<string>(fullCode);
	const prevTargetRef = useRef(target);
	if (target && target !== prevTargetRef.current) {
		originalCodeRef.current = fullCode;
	}
	prevTargetRef.current = target;

	// Splice the edited snippet into the full code at the target line range
	const spliceSnippet = useCallback(
		(snippet: string) => {
			if (!target) return;
			const lines = originalCodeRef.current.split("\n");
			const before = lines.slice(0, target.lineNumber);
			const after = lines.slice(target.endLineNumber);
			return [...before, ...snippet.split("\n"), ...after].join("\n");
		},
		[target],
	);

	// Live-update the preview on every keystroke
	const handleDocChange = useCallback(
		(code: string) => {
			const newCode = spliceSnippet(code);
			if (newCode != null) {
				fireUpdateCodeEvent(newCode);
			}
		},
		[spliceSnippet],
	);

	const handleApply = useCallback(() => {
		if (!target || !editorViewRef.current) return;
		// Preview is already up to date from live editing.
		// Fire one final update to ensure the main CM is in sync.
		const editedCode = editorViewRef.current.state.doc.toString();
		const newCode = spliceSnippet(editedCode);
		if (newCode != null) {
			fireUpdateCodeEvent(newCode);
		}
		setTarget(null);
		setLevel(1);
	}, [target, spliceSnippet, setTarget, setLevel]);

	const handleCancel = useCallback(() => {
		// Restore the original code in the main CM / preview
		fireUpdateCodeEvent(originalCodeRef.current);
		setTarget(null);
		setLevel(1);
	}, [setTarget, setLevel]);

	if (!target) {
		return (
			<InlineEditorContainer
				id="panel-edit"
				role="tabpanel"
				aria-labelledby="tab-edit-element"
			>
				<InlineEditorContext>
					Click an element in the preview to edit it here.
				</InlineEditorContext>
			</InlineEditorContainer>
		);
	}

	return (
		<InlineEditorContainer
			id="panel-edit"
			role="tabpanel"
			aria-labelledby="tab-edit-element"
		>
			{/* <InlineEditorContext id="element-context">
				Editing {target.elementType} on page {target.pageIndex + 1}
			</InlineEditorContext> */}
			<InlineCodeMirrorWrapper>
				<InlineCodeMirror
					initialCode={target.jadleSnippet}
					editorViewRef={editorViewRef}
					onDocChange={handleDocChange}
				/>
			</InlineCodeMirrorWrapper>
			<InlineEditorActions>
				<InlineEditorButton
					$variant="secondary"
					onClick={handleCancel}
					aria-label="Cancel editing"
				>
					Cancel
				</InlineEditorButton>
				<InlineEditorButton
					$variant="primary"
					onClick={handleApply}
					aria-label="Apply changes"
				>
					Apply
				</InlineEditorButton>
			</InlineEditorActions>
		</InlineEditorContainer>
	);
}

export default InlineElementEditor;
